import type { KeyboardEvent, RefObject } from 'react';

interface UseKeyboardNavigationProps {
  onSelect?: (element: HTMLElement) => void;
  searchInputRef: RefObject<HTMLInputElement>;
  listRef: RefObject<HTMLUListElement>;
}

/**
 * Handles keyboard navigation for a search input and a list of options
 * @param onSelect - The function to call when an option is selected
 * @param searchInputRef - The ref to the search input
 * @param listRef - The ref to the list of options
 * @returns An object with two functions: handleSearchKeyDown and handleListKeyDown
 */
export const useKeyboardNavigation = ({
  onSelect,
  searchInputRef,
  listRef
}: UseKeyboardNavigationProps) => {
  const focusSearchInput = () => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const getOptions = () => {
    if (!listRef.current) {
      return [];
    }
    return Array.from(listRef.current.querySelectorAll('[role="option"]'));
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const options = getOptions();
      if (options.length > 0) {
        (options[0] as HTMLElement).focus();
      }
    }
  };

  const handleListKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const options = getOptions();
    const currentIndex = options.findIndex(
      (option) => option === document.activeElement
    );

    // Jump to the search input when a character is typed, or backspace is pressed
    if (
      (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) ||
      e.key === 'Backspace'
    ) {
      focusSearchInput();
      if (e.key === 'Backspace' && searchInputRef.current) {
        const newEvent = new KeyboardEvent('keydown', {
          key: e.key,
          ctrlKey: e.ctrlKey,
          altKey: e.altKey,
          metaKey: e.metaKey,
          bubbles: true
        });
        searchInputRef.current.dispatchEvent(newEvent);
      }
      return;
    }

    // When the enter key is pressed, select the current option
    if (e.key === 'Enter' && document.activeElement) {
      e.preventDefault();
      if (onSelect) {
        onSelect(document.activeElement as HTMLElement);
      } else {
        (document.activeElement as HTMLElement).click();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % options.length;
      (options[nextIndex] as HTMLElement).focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (currentIndex === 0) {
        focusSearchInput();
      } else {
        const nextIndex = (currentIndex - 1 + options.length) % options.length;
        (options[nextIndex] as HTMLElement).focus();
      }
    }
  };

  return {
    handleSearchKeyDown,
    handleListKeyDown
  };
};
